import React from 'react';
import {connect} from 'react-redux';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {
  makeGetAreAllSpam,
  makeGetAreAllTrashed,
} from '../../../../store/data/conversations/conversationsMultiSelectors';
import {ConversationBasesSelector} from '../../../../store/data/conversations/conversationsSelectors';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getIsSomeSelectAllJobRunning} from '../../../../store/data/jobs/jobsSelectors';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/lazyTeammatesSelectors';
import {getIsUserVisitor} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {CellsContext} from '../../../../store/panels/cells/context/cellsContext';
import {InboxesVisualConversationSelection} from '../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {makeGetKnownSelectedConversations} from '../../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {buildParametersSearchLocalFilterName} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersHelpers';
import {CellsPanelId, ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {makeGetIsBulkActionRequired} from '../../../../store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {
  clearSelectedConversationsAction,
  selectAllConversationsAction,
} from '../../../../store/stateless/navigationActions';
import {MultiConversationsViewerContent} from './multiConversationsViewerContent';

/*
 * Props.
 */

interface MultiConversationsViewerContentContainerProps {
  panelId: ViewerPanelId;
  cellsPanelId: CellsPanelId | undefined;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Constants.
 */

const searchSelectionName = <Trans id="workspace-search">this search</Trans>;

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: MultiConversationsViewerContentContainerProps) {
  return props.cellsPanelId;
}

function getCellsContext(_state: unknown, props: MultiConversationsViewerContentContainerProps) {
  return props.cellsContext;
}

function getInboxesParameters(_state: unknown, props: MultiConversationsViewerContentContainerProps) {
  return props.inboxesParameters;
}

function getConversationSelection(_state: unknown, props: MultiConversationsViewerContentContainerProps) {
  return props.conversationSelection;
}

function makeGetSelectionName() {
  return createSelector(
    [getInboxesParameters, getInboxes, getTags, getLenses, getFindTeammate, getTeams],
    (inboxesParameters, inboxes, tags, lenses, findTeammate, teams) => {
      if (hasSearchInInboxesParameters(inboxesParameters)) {
        return searchSelectionName;
      }

      return buildParametersSearchLocalFilterName(
        inboxes,
        tags,
        lenses,
        findTeammate,
        teams,
        inboxesParameters,
      );
    },
  );
}

export function makeGetAreAllSpamOrTrashed(
  getSelectedConversations: ConversationBasesSelector<MultiConversationsViewerContentContainerProps>,
) {
  const getAreAllSpam = makeGetAreAllSpam(getCellsContext, getSelectedConversations);
  const getAreAllTrashed = makeGetAreAllTrashed(getCellsContext, getSelectedConversations);
  return createSelector(
    [getAreAllSpam, getAreAllTrashed],
    (areAllSpam, areAllTrash) => areAllSpam || areAllTrash,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MultiConversationsViewerContentContainerProps>()(() => {
  const getSelectedConversations = makeGetKnownSelectedConversations(
    getConversationSelection,
    getCellsPanelId,
  );

  return {
    conversations: getSelectedConversations,
    selectionName: makeGetSelectionName(),
    areAllSpamOrTrashed: makeGetAreAllSpamOrTrashed(getSelectedConversations),
    companyUrl: getCompanyUrl,
    isBulkActionRequired: makeGetIsBulkActionRequired(getInboxesParameters, getConversationSelection),
    isBulkActionInProgress: getIsSomeSelectAllJobRunning,
    shouldHideActionButtons: getIsUserVisitor,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<MultiConversationsViewerContentContainerProps>()(
  ({cellsContext}) => ({
    onSelectAllClick: () => {
      trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
        cta_name: 'select_all',
      });

      return selectAllConversationsAction(cellsContext);
    },
    onClearSelectionClick: () => {
      trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
        cta_name: 'clear_selection',
      });

      return clearSelectedConversationsAction(cellsContext);
    },
  }),
);

export const MultiConversationsViewerContentContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MultiConversationsViewerContent);
